import { api } from './api.js';
import { config } from './config.js';
import { telemetry } from './telemetry.js';

const els = {
  searchSection: document.getElementById('searchSection'),
  resultsSection: document.getElementById('resultsSection'),
  featureUnavailable: document.getElementById('venueFeatureUnavailable'),
  postcode: document.getElementById('searchPostcode'),
  radius: document.getElementById('searchRadius'),
  quizType: document.getElementById('searchQuizType'),
  q: document.getElementById('searchQuery'),
  searchBtn: document.getElementById('searchBtn'),
  resetBtn: document.getElementById('resetBtn'),
  results: document.getElementById('venueResults'),
  empty: document.getElementById('venueEmpty'),

  enquiryModal: document.getElementById('enquiryModal'),
  enquiryForm: document.getElementById('enquiryForm'),
  enquiryVenueSlug: document.getElementById('enquiryVenueSlug'),
  enquiryName: document.getElementById('enquiryName'),
  enquiryEmail: document.getElementById('enquiryEmail'),
  enquiryPhone: document.getElementById('enquiryPhone'),
  enquiryDate: document.getElementById('enquiryDate'),
  enquiryMessage: document.getElementById('enquiryMessage'),
  enquiryHoneypot: document.getElementById('enquiryHoneypot'),
  enquirySubmitBtn: document.getElementById('enquirySubmitBtn'),
  enquiryStatus: document.getElementById('enquiryStatus'),
  enquiryNameError: document.getElementById('enquiryNameError'),
  enquiryEmailError: document.getElementById('enquiryEmailError'),
};

const state = {
  capabilities: {
    publicDirectoryEnabled: false,
  },
  submittingEnquiry: false,
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/London',
    });
  } catch {
    return '';
  }
};

const hideEnquiryErrors = () => {
  [els.enquiryNameError, els.enquiryEmailError].forEach((el) => {
    if (!el) return;
    el.textContent = '';
    el.classList.remove('show');
  });
};

const showFieldError = (el, message) => {
  if (!el) return;
  el.textContent = message;
  el.classList.add('show');
};

const openEnquiryModal = (slug) => {
  if (!slug || !els.enquiryModal) return;
  els.enquiryForm.reset();
  els.enquiryVenueSlug.value = slug;
  els.enquiryStatus.textContent = '';
  hideEnquiryErrors();
  els.enquiryModal.classList.add('active');
  document.body.style.overflow = 'hidden';
  window.setTimeout(() => els.enquiryName?.focus(), 0);
};

const closeEnquiryModal = () => {
  if (!els.enquiryModal) return;
  els.enquiryModal.classList.remove('active');
  document.body.style.overflow = '';
  state.submittingEnquiry = false;
  if (els.enquirySubmitBtn) {
    els.enquirySubmitBtn.disabled = false;
    els.enquirySubmitBtn.textContent = 'Send enquiry';
  }
};

const validateEnquiry = () => {
  hideEnquiryErrors();
  const name = els.enquiryName.value.trim();
  const email = els.enquiryEmail.value.trim();

  let valid = true;
  if (!name) {
    showFieldError(els.enquiryNameError, 'Name is required');
    valid = false;
  }
  if (!email) {
    showFieldError(els.enquiryEmailError, 'Email is required');
    valid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    showFieldError(els.enquiryEmailError, 'Enter a valid email address');
    valid = false;
  }

  return valid;
};

const setFeatureUnavailable = () => {
  if (els.featureUnavailable) {
    els.featureUnavailable.style.display = '';
  }
  if (els.searchSection) {
    els.searchSection.style.display = 'none';
  }
  if (els.resultsSection) {
    els.resultsSection.style.display = 'none';
  }
};

const setFeatureAvailable = () => {
  if (els.featureUnavailable) {
    els.featureUnavailable.style.display = 'none';
  }
  if (els.searchSection) {
    els.searchSection.style.display = '';
  }
  if (els.resultsSection) {
    els.resultsSection.style.display = '';
  }
};

const loadCapabilities = async () => {
  try {
    const response = await fetch(`${config.apiUrl}${config.endpoints.publicCapabilities}`);
    if (!response.ok) {
      state.capabilities.publicDirectoryEnabled = false;
      return;
    }

    const payload = await response.json().catch(() => null);
    state.capabilities.publicDirectoryEnabled = Boolean(payload?.data?.publicDirectoryEnabled);
  } catch {
    state.capabilities.publicDirectoryEnabled = false;
  }
};

const render = (rows) => {
  els.results.innerHTML = '';
  if (!rows.length) {
    els.empty.style.display = '';
    return;
  }

  els.empty.style.display = 'none';

  rows.forEach((venue) => {
    const card = document.createElement('article');
    card.className = 'history-item';
    card.innerHTML = `
      <div style="display:flex;justify-content:space-between;gap:1rem;flex-wrap:wrap;">
        <div>
          <h3 style="margin:0 0 0.25rem 0;">${venue.displayName || 'Venue'}</h3>
          <p style="margin:0;color:#64748b;">${venue.city || ''} ${venue.postcode || ''}</p>
          ${venue.description ? `<p style="margin-top:0.5rem;color:#334155;">${venue.description}</p>` : ''}
          ${Array.isArray(venue.quizTypes) && venue.quizTypes.length ? `<p style="margin-top:0.5rem;font-size:0.85rem;color:#475569;">Quiz types: ${venue.quizTypes.join(', ')}</p>` : ''}
          ${venue.stats ? `<p style="margin-top:0.35rem;font-size:0.85rem;color:#475569;">Avg teams: ${venue.stats.avgTeamsBand || 'n/a'} · Frequency: ${venue.stats.frequencyBand || 'n/a'}</p>` : ''}
          ${venue.nextEvent ? `<p style="margin-top:0.35rem;font-size:0.85rem;color:#475569;">Next event: ${venue.nextEvent.title || 'Quiz Night'} (${formatDate(venue.nextEvent.startsAt)})</p>` : ''}
        </div>
        <div style="text-align:right;min-width:200px;">
          <p style="margin:0;font-weight:600;">${Number.isFinite(venue.distanceMiles) ? `${venue.distanceMiles} miles` : 'Distance n/a'}</p>
          <button class="btn btn-outline" style="margin-top:0.5rem;" data-enquire="${venue.slug}" type="button">Enquire</button>
        </div>
      </div>
    `;

    els.results.appendChild(card);
  });

  els.results.querySelectorAll('[data-enquire]').forEach((button) => {
    button.addEventListener('click', () => {
      const slug = button.getAttribute('data-enquire');
      openEnquiryModal(slug);
    });
  });
};

const search = async () => {
  if (!state.capabilities.publicDirectoryEnabled) {
    return;
  }

  els.searchBtn.disabled = true;
  els.searchBtn.textContent = 'Searching...';

  const params = new URLSearchParams({ page: '1', limit: '30' });
  const postcode = els.postcode.value.trim();
  if (postcode) params.set('postcode', postcode);
  params.set('radiusMiles', els.radius.value || '10');

  const quizType = els.quizType.value.trim();
  if (quizType) params.set('quizType', quizType);

  const q = els.q.value.trim();
  if (q) params.set('q', q);

  try {
    const response = await api.get(`${config.endpoints.publicVenueSearch}?${params.toString()}`);
    render(response.data || []);
  } catch (error) {
    els.results.innerHTML = `<p style="color:#b91c1c;">${error.message || 'Search failed'}</p>`;
    els.empty.style.display = 'none';
  } finally {
    els.searchBtn.disabled = false;
    els.searchBtn.textContent = 'Search';
  }
};

const submitEnquiry = async (event) => {
  event.preventDefault();
  if (state.submittingEnquiry) {
    return;
  }

  if (!validateEnquiry()) {
    return;
  }

  const slug = els.enquiryVenueSlug.value;
  if (!slug) {
    els.enquiryStatus.textContent = 'Missing venue context for this enquiry.';
    els.enquiryStatus.style.color = '#b91c1c';
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const payload = {
    name: els.enquiryName.value.trim(),
    email: els.enquiryEmail.value.trim(),
    phone: els.enquiryPhone.value.trim() || undefined,
    eventInterestDate: els.enquiryDate.value || undefined,
    message: els.enquiryMessage.value.trim() || undefined,
    honeypot: els.enquiryHoneypot.value || '',
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmTerm: params.get('utm_term') || undefined,
    utmContent: params.get('utm_content') || undefined,
    referrer: document.referrer || undefined,
  };

  try {
    state.submittingEnquiry = true;
    els.enquirySubmitBtn.disabled = true;
    els.enquirySubmitBtn.textContent = 'Sending...';
    els.enquiryStatus.textContent = '';

    await api.post(config.endpoints.publicVenueEnquiry(slug), payload);
    telemetry.track('enquiry_submitted', {
      source: 'public_venue_directory',
      slug,
    });
    els.enquiryStatus.textContent = 'Thanks, your enquiry has been sent.';
    els.enquiryStatus.style.color = '#047857';
    window.setTimeout(() => {
      closeEnquiryModal();
    }, 900);
  } catch (error) {
    els.enquiryStatus.textContent = error.message || 'Could not send enquiry';
    els.enquiryStatus.style.color = '#b91c1c';
  } finally {
    state.submittingEnquiry = false;
    els.enquirySubmitBtn.disabled = false;
    els.enquirySubmitBtn.textContent = 'Send enquiry';
  }
};

const bindEvents = () => {
  els.searchBtn?.addEventListener('click', () => {
    search().catch(() => {});
  });

  els.resetBtn?.addEventListener('click', () => {
    els.postcode.value = '';
    els.radius.value = '10';
    els.quizType.value = '';
    els.q.value = '';
    els.results.innerHTML = '';
    els.empty.style.display = 'none';
  });

  els.enquiryForm?.addEventListener('submit', (event) => {
    submitEnquiry(event).catch(() => {});
  });

  document.querySelectorAll('[data-enquiry-close]').forEach((btn) => {
    btn.addEventListener('click', () => closeEnquiryModal());
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && els.enquiryModal?.classList.contains('active')) {
      closeEnquiryModal();
    }
  });
};

const bootstrap = async () => {
  bindEvents();
  await loadCapabilities();

  if (!state.capabilities.publicDirectoryEnabled) {
    setFeatureUnavailable();
    return;
  }

  setFeatureAvailable();
  await search();
};

bootstrap().catch(() => {
  setFeatureUnavailable();
});
